import { supabase } from '@/lib/supabase'
import type { AffiliateOverview, AffiliateTimeseriesPoint, ReferredAccountRow, CommissionRow } from '../types'

export async function getAffiliateOverview(affiliateUserId: string): Promise<AffiliateOverview | null> {
  if (!affiliateUserId) return null
  const [overview, upline] = await Promise.all([
    supabase
      .from('v_affiliate_master_overview')
      .select('*')
      .eq('affiliate_user_id', affiliateUserId)
      .maybeSingle(),
    supabase
      .from('v_affiliate_upline_active_overview')
      .select('affiliate_user_id, upline_active_subscriptions_count')
      .eq('affiliate_user_id', affiliateUserId)
      .maybeSingle(),
  ])
  if (overview.error) throw overview.error
  if (upline.error) throw upline.error
  const base = (overview.data as any) || null
  if (!base) return null
  const up = (upline.data as any) || null
  if (up) {
    base.upline_active_subscriptions_count = Number(up.upline_active_subscriptions_count || 0)
  }
  return base as AffiliateOverview
}

export async function getMyAffiliateOverview(): Promise<AffiliateOverview | null> {
  const { data, error } = await supabase.functions.invoke('affiliate-overview', { body: {} as any })
  if (error) throw error
  return (data?.overview as any) || null
}

export async function getAffiliateTimeseries(affiliateUserId: string, days = 30): Promise<AffiliateTimeseriesPoint[]> {
  const fromDate = new Date(Date.now() - days * 24 * 60 * 60 * 1000)
  const { data, error } = await supabase
    .from('v_affiliate_revenue_timeseries')
    .select('day, revenue_cents, commissions_matured_cents, new_accounts_count')
    .eq('affiliate_user_id', affiliateUserId)
    .gte('day', fromDate.toISOString())
    .order('day', { ascending: true })
  if (error) throw error
  return (data as any[])?.map((r: any) => ({
    day: typeof r.day === 'string' ? r.day : new Date(r.day).toISOString(),
    revenue_cents: Number(r.revenue_cents || 0),
    commissions_matured_cents: Number(r.commissions_matured_cents || 0),
    new_accounts_count: Number(r.new_accounts_count || 0),
  })) || []
}

export async function listReferredAccounts(affiliateUserId: string, limit = 20): Promise<ReferredAccountRow[]> {
  const { data, error } = await supabase
    .from('v_affiliate_referred_accounts')
    .select('account_id, account_name, created_at, subscription_status, current_period_end')
    .eq('affiliate_user_id', affiliateUserId)
    .order('created_at', { ascending: false })
    .limit(limit)
  if (error) throw error
  return (data as any) || []
}

export async function listReferredAccountsPaged(affiliateUserId: string, page = 1, pageSize = 25): Promise<{ rows: ReferredAccountRow[]; total: number }> {
  const from = Math.max(0, (page - 1) * pageSize)
  const to = Math.max(0, from + pageSize - 1)
  const { data, error, count } = await supabase
    .from('v_affiliate_referred_accounts')
    .select('account_id, account_name, created_at, subscription_status, current_period_end', { count: 'exact' })
    .eq('affiliate_user_id', affiliateUserId)
    .order('created_at', { ascending: false })
    .range(from, to)
  if (error) throw error
  return { rows: (data as any) || [], total: Number(count || 0) }
}

export async function listAffiliateCommissions(affiliateUserId: string, limit = 20): Promise<CommissionRow[]> {
  const { data, error } = await supabase
    .from('v_affiliate_commissions_detailed')
    .select('commission_id, account_id, subscription_id, amount_cents, currency, status, period_start, period_end, matures_at, matured_at, paid_at, payout_id')
    .eq('affiliate_user_id', affiliateUserId)
    .order('period_end', { ascending: false })
    .limit(limit)
  if (error) throw error
  const rows = (data as any[]) || []
  if (!rows.length) return []
  // Filtra comissões de contas inexistentes (cliente deletado)
  const accIds = Array.from(new Set(rows.map(r => r.account_id).filter(Boolean))) as string[]
  if (!accIds.length) return []
  try {
    const { data: existing } = await supabase
      .from('v_affiliate_referred_accounts')
      .select('account_id')
      .eq('affiliate_user_id', affiliateUserId)
      .in('account_id', accIds)
    const keep = new Set(((existing || []) as any[]).map(r => String(r.account_id)))
    return rows.filter(r => keep.has(String(r.account_id || ''))) as any
  } catch {
    return []
  }
}

// Paged commissions with exact count (no extra account filtering). Useful for master view pagination.
export async function listAffiliateCommissionsPagedCounted(
  affiliateUserId: string,
  page = 1,
  pageSize = 25
): Promise<{ rows: CommissionRow[]; total: number }> {
  const from = Math.max(0, (page - 1) * pageSize)
  const to = Math.max(0, from + pageSize - 1)
  const { data, error, count } = await supabase
    .from('v_affiliate_commissions_detailed')
    .select(
      'commission_id, account_id, subscription_id, amount_cents, currency, status, period_start, period_end, matures_at, matured_at, paid_at, payout_id',
      { count: 'exact' }
    )
    .eq('affiliate_user_id', affiliateUserId)
    .order('period_end', { ascending: false })
    .range(from, to)
  if (error) throw error
  return { rows: ((data as any[]) || []) as any, total: Number(count || 0) }
}

export async function listAffiliateCommissionsPaged(affiliateUserId: string, page = 1, pageSize = 25): Promise<{ rows: CommissionRow[]; total: number }> {
  const from = Math.max(0, (page - 1) * pageSize)
  const to = Math.max(0, from + pageSize - 1)
  const { data, error } = await supabase
    .from('v_affiliate_commissions_detailed')
    .select('commission_id, account_id, subscription_id, amount_cents, currency, status, period_start, period_end, matures_at, matured_at, paid_at, payout_id')
    .eq('affiliate_user_id', affiliateUserId)
    .order('period_end', { ascending: false })
    .range(from, to)
  if (error) throw error
  const rows = (data as any[]) || []
  if (!rows.length) return { rows, total: 0 }
  // Filtra comissões de contas inexistentes (cliente deletado) para não exibir "-" ao afiliado
  const accIds = Array.from(new Set(rows.map(r => r.account_id).filter(Boolean))) as string[]
  let keep = new Set<string>()
  if (accIds.length) {
    try {
      const { data: existing } = await supabase
        .from('v_affiliate_referred_accounts')
        .select('account_id')
        .eq('affiliate_user_id', affiliateUserId)
        .in('account_id', accIds)
      keep = new Set(((existing || []) as any[]).map(r => String(r.account_id)))
    } catch {}
  }
  const filtered = accIds.length ? rows.filter(r => keep.has(String(r.account_id || ''))) : []
  return { rows: filtered as any, total: filtered.length }
}

export async function getMyAffiliateBundle(): Promise<{ overview: AffiliateOverview|null, series: AffiliateTimeseriesPoint[], referred: ReferredAccountRow[], commissions: CommissionRow[] }> {
  const { data, error } = await supabase.functions.invoke('affiliate-overview', { body: {} as any })
  if (error) throw error
  return {
    overview: (data?.overview as any) || null,
    series: (data?.series as any) || [],
    referred: (data?.referred as any) || [],
    commissions: (data?.commissions as any) || [],
  }
}

export async function getAffiliateTrialFunnel(affiliateUserId: string): Promise<{
  referred_accounts_total: number
  trial_accounts_total: number
  trial_accounts_active: number
  paid_accounts_converted: number
} | null> {
  const { data, error } = await supabase
    .from('v_affiliate_trial_funnel')
    .select('referred_accounts_total, trial_accounts_total, trial_accounts_active, paid_accounts_converted')
    .eq('affiliate_user_id', affiliateUserId)
    .maybeSingle()
  if (error) throw error
  if (!data) return null
  const row = data as any
  return {
    referred_accounts_total: Number(row.referred_accounts_total || 0),
    trial_accounts_total: Number(row.trial_accounts_total || 0),
    trial_accounts_active: Number(row.trial_accounts_active || 0),
    paid_accounts_converted: Number(row.paid_accounts_converted || 0),
  }
}

// Agrega totais de comissão diretamente da view detalhada para ter compatibilidade entre
// ambientes onde o status pode estar 'matured' ou 'earned'. Exclui comissões já pagas.
export async function computeAffiliateCommissionTotals(affiliateUserId: string): Promise<{
  pending_cents: number
  ready_to_pay_cents: number // matured/earned e não pagas
  accrued_cents: number // pending + ready_to_pay
}> {
  let from = 0
  const chunk = 1000
  let pending = 0
  let ready = 0
  for (;;) {
    const { data, error } = await supabase
      .from('v_affiliate_commissions_detailed')
      .select('amount_cents, status, paid_at')
      .eq('affiliate_user_id', affiliateUserId)
      .order('period_end', { ascending: false })
      .range(from, from + chunk - 1)
    if (error) throw error
    const rows = (data as any[]) || []
    for (const r of rows) {
      const amt = Number(r?.amount_cents || 0)
      const st = String(r?.status || '').toLowerCase()
      const paid = !!r?.paid_at
      if (paid) continue
      if (st === 'pending') pending += amt
      if (st === 'matured' || st === 'earned') ready += amt
    }
    if (rows.length < chunk) break
    from += chunk
  }
  return { pending_cents: pending, ready_to_pay_cents: ready, accrued_cents: pending + ready }
}

// Conta quantas assinaturas do afiliado estão canceladas
export async function countAffiliateCanceledSubscriptions(affiliateUserId: string): Promise<number> {
  // 1) lista contas referidas pelo afiliado
  const { data: accounts, error: e1 } = await supabase
    .from('accounts')
    .select('id')
    .eq('referrer_affiliate_id', affiliateUserId)
  if (e1) throw e1
  const accIds = ((accounts || []) as any[]).map((a) => a.id).filter(Boolean)
  if (accIds.length === 0) return 0
  // 2) conta subscriptions canceladas dessas contas
  const { count, error: e2 } = await supabase
    .from('subscriptions')
    .select('id', { count: 'exact', head: true })
    .in('account_id', accIds)
    .eq('status', 'canceled')
  if (e2) throw e2
  return Number(count || 0)
}

// Estatísticas de pagamentos (payouts) do afiliado
export async function getAffiliatePayoutStats(affiliateUserId: string): Promise<{
  paid_count_month: number
  paid_sum_month_cents: number
  paid_sum_all_time_cents: number
}> {
  const now = new Date()
  const monthStart = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1, 0, 0, 0))
  const nextMonthStart = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1, 0, 0, 0))

  // Count pagos no mês
  let paid_count_month = 0
  {
    const { count, error } = await supabase
      .from('affiliate_payouts')
      .select('id', { count: 'exact', head: true })
      .eq('affiliate_user_id', affiliateUserId)
      .eq('status', 'paid')
      .gte('paid_at', monthStart.toISOString())
      .lt('paid_at', nextMonthStart.toISOString())
    if (error) throw error
    paid_count_month = Number(count || 0)
  }

  // Soma valores pagos (mês e total) em blocos
  let paid_sum_all_time_cents = 0
  let paid_sum_month_cents = 0
  const chunk = 1000
  for (let from = 0;; from += chunk) {
    const { data, error } = await supabase
      .from('affiliate_payouts')
      .select('amount_cents, paid_at')
      .eq('affiliate_user_id', affiliateUserId)
      .eq('status', 'paid')
      .order('paid_at', { ascending: false })
      .range(from, from + chunk - 1)
    if (error) throw error
    const rows = (data as any[]) || []
    for (const r of rows) {
      const amt = Number(r?.amount_cents || 0)
      paid_sum_all_time_cents += amt
      const paidAt = r?.paid_at ? new Date(r.paid_at) : null
      if (paidAt && paidAt >= monthStart && paidAt < nextMonthStart) paid_sum_month_cents += amt
    }
    if (rows.length < chunk) break
  }

  return { paid_count_month, paid_sum_month_cents, paid_sum_all_time_cents }
}
